import React, { useState, useCallback, useEffect } from 'react';

interface ListScroll {
  canScrollLeft: boolean;
  canScrollRight: boolean;
  updateScrollState: () => void;
  scrollBy: (direction: 1 | -1) => void;
  scrollLeft: () => void;
  scrollRight: () => void;
}

/**
 * hook to manage scroll buttons on a horizontal list
 */
const useListScroll = <T extends HTMLElement>(
  listRef: React.RefObject<T>,
  perScrollAmount = 300
): ListScroll => {
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);

  const updateScrollState = useCallback(() => {
    if (!listRef.current) {
      return;
    }

    const tolerance = 20;
    const { clientWidth, scrollWidth, scrollLeft } = listRef.current;
    setCanScrollLeft(scrollLeft > tolerance);
    setCanScrollRight(scrollLeft + clientWidth + tolerance < scrollWidth);
  }, [listRef]);

  const scrollBy: ListScroll['scrollBy'] = useCallback(
    (direction) => {
      if (!listRef.current) {
        return;
      }

      listRef.current.scrollBy({
        left: perScrollAmount * direction,
        behavior: 'smooth',
      });
    },
    [listRef, perScrollAmount]
  );

  const scrollLeft = useCallback(() => {
    scrollBy(-1);
  }, [scrollBy]);

  const scrollRight = useCallback(() => {
    scrollBy(1);
  }, [scrollBy]);

  useEffect(() => {
    updateScrollState();
  }, [updateScrollState, listRef]);

  return {
    canScrollLeft,
    canScrollRight,
    updateScrollState,
    scrollBy,
    scrollLeft,
    scrollRight,
  };
};

export default useListScroll;
